import { Component, EventEmitter, Input, Output } from '@angular/core';
import { LanguageService } from '../../../services/i18n/raksme.languageService';

/** The shape the gallery endpoints already return for one item. */
export interface MediaItem {
  image?: string;
  url?: string;
  caption?: string;
  gallery_type?: string;
}

/**
 * One gallery tile: image, hover overlay, type icon, and the lightbox link.
 *
 * The same block appears in 8 templates across both trees. Repeating it is how
 * the accessible name went missing: the lightbox link has no text of its own,
 * so every copy had to remember to name it, and none did until this week.
 *
 * The name comes from the item's caption - checked against the data rather than
 * assumed: all 119 rows in gallery_picture and videogallery_picture are plain
 * text, none contain markup. A static fallback covers the one row with an empty
 * caption, so the link is never anonymous.
 */
@Component({
  selector: 'raksme-media-tile',
  templateUrl: './media-tile.component.html',
  styleUrls: ['./media-tile.component.css']
})
export class MediaTileComponent {

  constructor(private language: LanguageService) { }

  @Input() item: MediaItem = {};

  /** Groups tiles for the vendor lightbox so prev/next walks the gallery. */
  @Input() group: string = 'media-group-1';

  /** 'ar' switches the fallback label; captions themselves come translated. */
  /**
   * Language for the built-in strings.
   *
   * Defaults to the language of the current route rather than to English, so a
   * caller on an Arabic page gets Arabic without having to say so. Every
   * `lang="ar"` in the templates existed only because this defaulted to 'en';
   * they are redundant now, and as the language fork retires (D3) a single
   * component serving both languages cannot pass a hardcoded value anyway.
   *
   * An explicit binding still wins, for the rare caller that needs to override.
   */
  @Input() set lang(v: string) { this._lang = v; }
  get lang(): string { return this._lang || this.language.lang; }
  private _lang: string = null;

  /** The caption heading is not shown on every surface that uses a tile. */
  @Input() showCaption: boolean = true;

  @Output() open = new EventEmitter<MediaItem>();

  get isVideo(): boolean {
    return (this.item && this.item.gallery_type === 'youtubevideos');
  }

  get iconClass(): string {
    return this.isVideo ? 'fa fa-play' : 'fa fa-picture-o';
  }

  get label(): string {
    // The caption holds HTML, so the name is its text: tags stripped and the
    // common entities decoded, or a screen reader would say "amp".
    const caption = (this.item && this.item.caption ? String(this.item.caption) : '')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ').replace(/&quot;/g, '"').replace(/&#0?39;/g, "'")
      .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')
      .trim();
    if (caption) { return caption; }
    if (this.lang === 'ar') { return this.isVideo ? 'تشغيل الفيديو' : 'عرض الصورة'; }
    return this.isVideo ? 'Play video' : 'View photo';
  }

  onOpen(): void {
    this.open.emit(this.item);
  }
}
